import type { Href } from "expo-router";
import { usePathname, useRouter } from "expo-router";
import { useCallback, useState, type ReactNode } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  type ViewStyle,
} from "react-native";
import Svg, { Circle, Line, Path, Rect } from "react-native-svg";
import { SafeAreaView } from "react-native-safe-area-context";

import { signOutToHome } from "../../lib/auth";
import { COLORS, FONTS, RADII, SPACING, STROKES } from "../../lib/brand";
import { LogoMark, LogoWordmark } from "../brand/Logo";
import { PANE_THEMES } from "../brand/paneThemes";
import { useShellResponsive } from "../../lib/useShellResponsive";
import type { CompetitionStatus } from "../../types/database";
import { HamburgerButton, ShellMobileDrawer } from "../ShellMobileDrawer";

// Client portal chrome is locked to the Client pane theme so editing
// /brand (Client) propagates here. Local const names are kept so the
// JSX below is untouched — only the source of each value changed.
const THEME = PANE_THEMES.Client;
const BLACK = THEME.pageBg;
const FOREST = THEME.sidebarBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = THEME.accent;
const LINEN = THEME.textOnSurface;
const SAGE = COLORS.SAGE.hex;
const GOLD = COLORS.GOLD_RM.hex;

const SIDEBAR_W = 220;
const TOPBAR_H = 52;

const W = "rgba(255,255,255,1)";
const BORDER = "rgba(255,255,255,0.07)";
const SECTION_LABEL = "rgba(237,232,223,0.30)";
const ACTIVE_PROGRAM_NAME = "rgba(237,232,223,0.60)";

export type OverviewNavKey = "dashboard" | "candidates" | "company" | "team" | "billing" | "settings";

export type ProgramNavKey =
  | "program-dashboard"
  | "program-edit"
  | "program-candidates"
  | "program-deliverables"
  | "program-positions"
  | "program-pipeline"
  | "program-shortlist";

/**
 * Per-program breadcrumb context. When passed, an "ACTIVE PROGRAM"
 * section renders below the Overview nav with program-scoped nav items.
 * Pages know they're in program context — the shell stays "dumb" and
 * never fetches anything itself.
 */
export type ActiveProgramContext = {
  id: string;
  name: string;
  // Use the central CompetitionStatus so we don't drift from the DB
  // enum. ProgramStatusPill below explicitly maps the four "live" states
  // (draft/open/judging/closed) and falls through to a neutral pill for
  // anything else (submitted/under_review/approved/etc — surfaced as
  // first-class visuals in the upcoming approval-flow chunks).
  status: CompetitionStatus;
  engagement_model: "licensing" | "rm";
};

export type OverviewIdentity = {
  /** "Acme Capital Management" — bottom-left of sidebar */
  companyName: string;
  /** "Jane Smith · Recruiting Lead" — line under company. May be just the name when no role title is set. */
  roleLine: string;
  /** "JS" — circle in topbar */
  initials: string;
};

export type OverviewShellProps = {
  children: ReactNode;
  /** Top-bar title text. Renders as Cormorant medium. */
  topbarTitle: string;
  /** Where this page sits in the Overview nav. Auto-derived from pathname when omitted. */
  activeKey?: OverviewNavKey;
  /** Program-context breadcrumb. Pass when the route is a /client/competition/[id]/* page. */
  activeProgram?: ActiveProgramContext;
  /** Which program-scoped nav item is active. Required to highlight a program nav row. */
  activeProgramKey?: ProgramNavKey;
  identity: OverviewIdentity;
  /**
   * Reduced-permission ("limited") client — the former Manager role. Hides
   * the company-management affordances (Your Company / Team / Billing) and
   * the per-program "Edit case" control. Purely cosmetic; RLS is the real
   * enforcement (a limited client is not a company_users member, so every
   * full-client write policy blocks them server-side).
   */
  limited?: boolean;
};

function navKeyFromPath(path: string): OverviewNavKey | null {
  const p = (path.split("?")[0] ?? "").replace(/\/+$/, "") || "/";
  if (p.endsWith("/client/candidates")) return "candidates";
  if (p.endsWith("/client/company")) return "company";
  if (p.endsWith("/client/team")) return "team";
  if (p.endsWith("/client/billing")) return "billing";
  if (p.endsWith("/client/settings")) return "settings";
  if (p === "/client" || p.endsWith("/client")) return "dashboard";
  return null;
}

/**
 * Coravu top-level Overview chrome — the single shell across the entire
 * client portal. Forest sidebar on the left, black topbar on top, content
 * area on near-black.
 *
 * The four Overview items (Dashboard / Candidates / Your Company / Settings)
 * are always visible. When the route is a program page, the caller passes
 * `activeProgram` and an "ACTIVE PROGRAM" section renders below Settings
 * with a program-scoped nav (full case-editor nav for Licensing programs,
 * a slimmer Status / Candidates / Shortlist set for Coravu RM programs).
 *
 * Desktop-first; the sidebar collapses behind the content on viewports < 900px.
 */
export function OverviewShell({
  children,
  topbarTitle,
  activeKey,
  activeProgram,
  activeProgramKey,
  identity,
  limited = false,
}: OverviewShellProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { isMobile, showSidebar, drawerOpen, openDrawer, closeDrawer } =
    useShellResponsive();
  const auto = navKeyFromPath(pathname);

  /** Closes the drawer (no-op on desktop) before navigating, so a tap on a
   *  nav row in the mobile overlay dismisses the panel and routes in one step. */
  const navTo = useCallback(
    (href: Href) => {
      closeDrawer();
      router.push(href);
    },
    [closeDrawer, router],
  );

  // When the user is inside a program, none of the Overview items get the
  // active highlight — the program-scoped section owns the active state.
  // navKeyFromPath returns null for program routes anyway, but the explicit
  // override keeps the contract obvious.
  const overviewActive: OverviewNavKey | null = activeProgram
    ? null
    : (activeKey ?? auto);

  const overviewIconColor = (key: OverviewNavKey) =>
    overviewActive === key ? LINEN : "rgba(237,232,223,0.42)";

  const programIconColor = (key: ProgramNavKey) =>
    activeProgramKey === key ? LINEN : "rgba(237,232,223,0.42)";

  const sidebarBody = (
    <>
      {/* Logo header → /client */}
      <Pressable
        onPress={() => navTo("/client" as Href)}
              style={[
                {
                  height: TOPBAR_H,
                  paddingHorizontal: 18,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                  borderBottomWidth: STROKES.hairline,
                  borderBottomColor: "rgba(181,135,58,0.12)",
                },
              ]}
            >
              <LogoMark size={28} tile={FOREST_MID} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} />
              <View style={{ minWidth: 0, flexShrink: 1 }}>
                <LogoWordmark
                  numberOfLines={1}
                  fontSize={17}
                  letterSpacing={3}
                  color={LINEN}
                  accent={AMBER}
                />
                <Text
                  numberOfLines={1}
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 8.5,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: "rgba(237,232,223,0.28)",
                    marginTop: 1,
                  }}
                >
                  Overview
                </Text>
              </View>
            </Pressable>

            {/* Scrollable nav region — overflow accommodates the active-program
                section without pushing the footer off-screen on short viewports. */}
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ paddingTop: SPACING.sm, paddingBottom: SPACING.sm }}
              showsVerticalScrollIndicator={false}
            >
              <SidebarNavItem
                label="Dashboard"
                active={overviewActive === "dashboard"}
                onPress={() => navTo("/client" as Href)}
                icon={<IconDashboard color={overviewIconColor("dashboard")} />}
              />
              <SidebarNavItem
                label="Candidates"
                active={overviewActive === "candidates"}
                onPress={() => navTo("/client/candidates" as Href)}
                icon={<IconCandidates color={overviewIconColor("candidates")} />}
              />
              {/* Company-management surfaces — full clients only. A limited
                  client (former Manager) is not a company member, so RLS
                  blocks these anyway; we hide them to keep the nav honest. */}
              {!limited ? (
                <>
                  <SidebarNavItem
                    label="Your Company"
                    active={overviewActive === "company"}
                    onPress={() => navTo("/client/company" as Href)}
                    icon={<IconCompany color={overviewIconColor("company")} />}
                  />
                  <SidebarNavItem
                    label="Team"
                    active={overviewActive === "team"}
                    onPress={() => navTo("/client/team" as Href)}
                    icon={<IconTeam color={overviewIconColor("team")} />}
                  />
                  <SidebarNavItem
                    label="Billing"
                    active={overviewActive === "billing"}
                    onPress={() => navTo("/client/billing" as Href)}
                    icon={<IconBilling color={overviewIconColor("billing")} />}
                  />
                </>
              ) : null}
              <SidebarNavItem
                label="Settings"
                active={overviewActive === "settings"}
                onPress={() => navTo("/client/settings" as Href)}
                icon={<IconSettings color={overviewIconColor("settings")} />}
              />

              {activeProgram ? (
                <ActiveProgramSection
                  program={activeProgram}
                  activeProgramKey={activeProgramKey}
                  iconColor={programIconColor}
                  onNavigate={(href) => navTo(href)}
                  limited={limited}
                />
              ) : null}
            </ScrollView>

            {/* Footer (company + role + sign out) */}
            <View
              style={{
                paddingHorizontal: 18,
                paddingVertical: SPACING.sm,
                borderTopWidth: STROKES.hairline,
                borderTopColor: "rgba(181,135,58,0.12)",
              }}
            >
              <Text
                style={{ fontFamily: FONTS.bodySemiBold, fontSize: 12, color: LINEN }}
                numberOfLines={1}
              >
                {identity.companyName}
              </Text>
              {identity.roleLine ? (
                <Text
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 10,
                    color: "rgba(237,232,223,0.30)",
                    marginTop: 1,
                  }}
                  numberOfLines={1}
                >
                  {identity.roleLine}
                </Text>
              ) : null}
              <Pressable
                onPress={() => {
                  closeDrawer();
                  void signOutToHome(router);
                }}
                style={[
                  { marginTop: 8, flexDirection: "row", alignItems: "center", gap: 5 },
                ]}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyMedium,
                    fontSize: 10.5,
                    color: "rgba(237,232,223,0.30)",
                  }}
                >
                  Sign out
                </Text>
              </Pressable>
            </View>
    </>
  );

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: BLACK }} edges={["top", "left", "right"]}>
      <View style={{ flex: 1, flexDirection: "row" }}>
        {showSidebar ? (
          <View
            style={{
              width: SIDEBAR_W,
              backgroundColor: FOREST,
              borderRightWidth: STROKES.hairline,
              borderRightColor: "rgba(181,135,58,0.12)",
              flexDirection: "column",
            } as ViewStyle}
          >
            {sidebarBody}
          </View>
        ) : null}

        {/* Right column: topbar + main */}
        <View style={{ flex: 1, minWidth: 0, backgroundColor: BLACK }}>
          <View
            style={[
              {
                height: TOPBAR_H,
                paddingHorizontal: isMobile ? 12 : 24,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                borderBottomWidth: STROKES.hairline,
                borderBottomColor: BORDER,
                backgroundColor: "rgba(14,14,14,0.97)",
              },
              Platform.OS === "web"
                ? ({ backdropFilter: "blur(16px)" } as unknown as ViewStyle)
                : null,
            ]}
          >
            <View style={{ flexDirection: "row", alignItems: "center", flex: 1, minWidth: 0 }}>
              {isMobile ? <HamburgerButton onPress={openDrawer} color={LINEN} /> : null}
              <Text
                style={{
                  fontFamily: FONTS.displayMedium,
                  fontSize: 17,
                  color: W,
                  flexShrink: 1,
                }}
                numberOfLines={1}
              >
                {topbarTitle}
              </Text>
            </View>
            <View
              style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                backgroundColor: FOREST_MID,
                borderWidth: STROKES.hairline,
                borderColor: "rgba(181,135,58,0.30)",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  fontFamily: FONTS.displaySemiBold,
                  fontSize: 12.5,
                  color: AMBER,
                }}
              >
                {identity.initials}
              </Text>
            </View>
          </View>

          <View style={{ flex: 1, minHeight: 0, backgroundColor: BLACK }}>
            {children}
          </View>
        </View>
      </View>

      <ShellMobileDrawer
        open={drawerOpen}
        onRequestClose={closeDrawer}
        backgroundColor={FOREST}
      >
        {sidebarBody}
      </ShellMobileDrawer>
    </SafeAreaView>
  );
}

/* ─────────────────────────  Active program section  ───────────────────────── */

function ActiveProgramSection({
  program,
  activeProgramKey,
  iconColor,
  onNavigate,
  limited = false,
}: {
  program: ActiveProgramContext;
  activeProgramKey: ProgramNavKey | undefined;
  iconColor: (key: ProgramNavKey) => string;
  onNavigate: (href: Href) => void;
  /** Limited (former-Manager) client: hide the "Edit case" control. */
  limited?: boolean;
}) {
  const isLicensing = program.engagement_model === "licensing";

  return (
    <View>
      {/* Thin divider — 16px above and below */}
      <View
        style={{
          height: 1,
          backgroundColor: "rgba(255,255,255,0.06)",
          marginVertical: 16,
          marginHorizontal: 18,
        }}
      />

      {/* Overline */}
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 8.5,
          letterSpacing: 2.4,
          textTransform: "uppercase",
          color: SECTION_LABEL,
          paddingHorizontal: 18,
          marginBottom: 6,
        }}
      >
        Active Program
      </Text>

      {/* Program name (grey-on-forest) */}
      <Text
        numberOfLines={2}
        ellipsizeMode="tail"
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 12.5,
          lineHeight: 17,
          color: ACTIVE_PROGRAM_NAME,
          paddingHorizontal: 18,
          marginBottom: 2,
        }}
      >
        {program.name}
      </Text>

      {/* Status pill + RM chip */}
      <View
        style={{
          paddingHorizontal: 18,
          marginBottom: 10,
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        <ProgramStatusPill status={program.status} />
        {!isLicensing ? <RmChip /> : null}
      </View>

      {/* 8px gap, then nav items. Pre-live cases (draft / denied / submitted /
          under_review / approved / contract_pending / contract_signed /
          payment_pending) hide the candidate/pipeline/shortlist/positions
          sections — those only have meaningful content once the case is
          live (open / judging / closed) or wrapping up
          (final_invoice_pending / final_invoice_paid). For LIC drafts that
          means the client only sees Edit case + Deliverables in the sidebar,
          which matches the "you're still building this thing" mental model. */}
      {(() => {
        const liveStatuses = [
          "open",
          "judging",
          "closed",
          "final_invoice_pending",
          "final_invoice_paid",
        ];
        const isLive = liveStatuses.includes(program.status as string);

        return isLicensing ? (
          <>
            {isLive ? (
              <SidebarNavItem
                label="Dashboard"
                active={activeProgramKey === "program-dashboard"}
                onPress={() => onNavigate(`/client/competition/${program.id}/dashboard` as Href)}
                icon={<IconDashboard color={iconColor("program-dashboard")} />}
              />
            ) : null}
            {!limited ? (
              <SidebarNavItem
                label="Edit case"
                active={activeProgramKey === "program-edit"}
                onPress={() => onNavigate(`/client/competition/${program.id}/edit` as Href)}
                icon={<IconEditor color={iconColor("program-edit")} />}
              />
            ) : null}
            {isLive ? (
              <SidebarNavItem
                label="Candidates"
                active={activeProgramKey === "program-candidates"}
                onPress={() => onNavigate(`/client/competition/${program.id}/candidates` as Href)}
                icon={<IconCandidates color={iconColor("program-candidates")} />}
              />
            ) : null}
            <SidebarNavItem
              label="Deliverables"
              active={activeProgramKey === "program-deliverables"}
              onPress={() => onNavigate(`/client/competition/${program.id}/deliverables` as Href)}
              icon={<IconDeliverables color={iconColor("program-deliverables")} />}
            />
            {isLive ? (
              <>
                <SidebarNavItem
                  label="Positions"
                  active={activeProgramKey === "program-positions"}
                  onPress={() => onNavigate(`/client/competition/${program.id}/positions` as Href)}
                  icon={<IconPositions color={iconColor("program-positions")} />}
                />
                <SidebarNavItem
                  label="Pipeline"
                  active={activeProgramKey === "program-pipeline"}
                  onPress={() => onNavigate(`/client/competition/${program.id}/pipeline` as Href)}
                  icon={<IconPipeline color={iconColor("program-pipeline")} />}
                />
                <SidebarNavItem
                  label="Shortlist"
                  active={activeProgramKey === "program-shortlist"}
                  onPress={() => onNavigate(`/client/competition/${program.id}/shortlist` as Href)}
                  icon={<IconShortlist color={iconColor("program-shortlist")} />}
                />
              </>
            ) : null}
          </>
        ) : (
          <>
            <SidebarNavItem
              label="Status"
              active={activeProgramKey === "program-dashboard"}
              onPress={() => onNavigate(`/client/competition/${program.id}/dashboard` as Href)}
              icon={<IconDashboard color={iconColor("program-dashboard")} />}
            />
            {isLive ? (
              <>
                <SidebarNavItem
                  label="Candidates"
                  active={activeProgramKey === "program-candidates"}
                  onPress={() => onNavigate(`/client/competition/${program.id}/candidates` as Href)}
                  icon={<IconCandidates color={iconColor("program-candidates")} />}
                />
                <SidebarNavItem
                  label="Positions"
                  active={activeProgramKey === "program-positions"}
                  onPress={() => onNavigate(`/client/competition/${program.id}/positions` as Href)}
                  icon={<IconPositions color={iconColor("program-positions")} />}
                />
                <SidebarNavItem
                  label="Pipeline"
                  active={activeProgramKey === "program-pipeline"}
                  onPress={() => onNavigate(`/client/competition/${program.id}/pipeline` as Href)}
                  icon={<IconPipeline color={iconColor("program-pipeline")} />}
                />
                <SidebarNavItem
                  label="Shortlist"
                  active={activeProgramKey === "program-shortlist"}
                  onPress={() => onNavigate(`/client/competition/${program.id}/shortlist` as Href)}
                  icon={<IconShortlist color={iconColor("program-shortlist")} />}
                />
              </>
            ) : null}
          </>
        );
      })()}
    </View>
  );
}

function ProgramStatusPill({ status }: { status: ActiveProgramContext["status"] }) {
  // Match CaseSwitcher's StatusPill palette so users transitioning from the
  // old shell don't have to re-learn color → status.
  const label =
    status === "draft" ? "Draft" : status === "open" ? "Open" : status === "judging" ? "Judging" : "Closed";
  const color =
    status === "draft"
      ? "rgba(255,255,255,0.5)"
      : status === "open"
        ? SAGE
        : status === "judging"
          ? AMBER
          : "rgba(255,255,255,0.35)";
  return (
    <View
      style={{
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: RADII.full,
        borderWidth: STROKES.hairline,
        borderColor: color,
        backgroundColor: "rgba(255,255,255,0.04)",
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 8,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          color,
        }}
      >
        {label}
      </Text>
    </View>
  );
}

function RmChip() {
  return (
    <View
      style={{
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: RADII.full,
        backgroundColor: "rgba(201,162,74,0.20)",
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: GOLD,
        }}
      >
        RM
      </Text>
    </View>
  );
}

/* ─────────────────────────  Sidebar primitives  ───────────────────────── */

function SidebarNavItem({
  label,
  icon,
  active,
  onPress,
  badge,
}: {
  label: string;
  icon: ReactNode;
  active: boolean;
  onPress: () => void;
  badge?: number;
}) {
  const [hover, setHover] = useState(false);
  const isWebHover = Platform.OS === "web" && hover && !active;

  return (
    <Pressable
      onPress={onPress}
      {...(Platform.OS === "web"
        ? ({ onHoverIn: () => setHover(true), onHoverOut: () => setHover(false) } as const)
        : {})}
      style={[
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 9,
          paddingHorizontal: 18,
          paddingVertical: SPACING.xs,
          borderLeftWidth: STROKES.strong,
          borderLeftColor: active ? AMBER : "transparent",
          backgroundColor: active
            ? THEME.accentTintSoft
            : isWebHover
              ? THEME.neutralHoverBgOnDark
              : "transparent",
          marginBottom: 1,
        } as ViewStyle,
        Platform.OS === "web"
          ? ({
              transition: "background-color 120ms, color 120ms",
            } as object)
          : null,
      ]}
    >
      <View style={{ opacity: active ? 1 : 0.5 }}>{icon}</View>
      <Text
        style={{
          flex: 1,
          fontFamily: FONTS.bodyMedium,
          fontSize: 12.5,
          color: active ? LINEN : "rgba(237,232,223,0.42)",
        }}
        numberOfLines={1}
      >
        {label}
      </Text>
      {badge != null && badge > 0 ? (
        <View
          style={{
            backgroundColor: AMBER,
            paddingHorizontal: 6,
            paddingVertical: 1,
            borderRadius: RADII.md,
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, color: FOREST }}>
            {badge > 99 ? "99+" : String(badge)}
          </Text>
        </View>
      ) : null}
    </Pressable>
  );
}

/* ─────────────────────────  Icons  ───────────────────────── */

function IconDashboard({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Rect x={1} y={1} width={5} height={5} rx={1} stroke={color} strokeWidth={1.1} />
      <Rect x={8} y={1} width={5} height={5} rx={1} stroke={color} strokeWidth={1.1} />
      <Rect x={1} y={8} width={5} height={5} rx={1} stroke={color} strokeWidth={1.1} />
      <Rect x={8} y={8} width={5} height={5} rx={1} stroke={color} strokeWidth={1.1} />
    </Svg>
  );
}

function IconCandidates({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Circle cx={7} cy={5} r={2.4} stroke={color} strokeWidth={1.1} />
      <Path
        d="M2.5 12c0-2.2 2-3.6 4.5-3.6S11.5 9.8 11.5 12"
        stroke={color}
        strokeWidth={1.1}
        strokeLinecap="round"
      />
    </Svg>
  );
}

function IconCompany({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Path d="M1.5 12.5h11" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
      <Path d="M2.5 12.5V4.5L7 2l4.5 2.5v8" stroke={color} strokeWidth={1.1} strokeLinejoin="round" />
      <Path d="M5 12.5V6.5M7 12.5V6.5M9 12.5V6.5" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
    </Svg>
  );
}

function IconTeam({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Circle cx={5} cy={5} r={2} stroke={color} strokeWidth={1.1} />
      <Circle cx={10.5} cy={5.5} r={1.6} stroke={color} strokeWidth={1.1} />
      <Path d="M1.5 12c0-2 1.5-3.2 3.5-3.2s3.5 1.2 3.5 3.2" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
      <Path d="M9 9.5c1.5 0 3.5 0.7 3.5 2.5" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
    </Svg>
  );
}

function IconBilling({ color }: { color: string }) {
  // Receipt with a centered "$" mark — line-weight 1.1 to match the rest
  // of the client-portal icon set.
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Path
        d="M3 1.5h8v11l-1.6-1-1.6 1-1.4-1-1.4 1L3.4 11.5l-1.4 1V1.5z"
        stroke={color}
        strokeWidth={1.1}
        strokeLinejoin="round"
      />
      <Path d="M7 4v6" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
      <Path
        d="M8.6 5.2c-.4-.5-1-.7-1.6-.7-.9 0-1.4.4-1.4 1 0 1.5 3 .8 3 2.4 0 .7-.7 1.1-1.6 1.1-.7 0-1.3-.2-1.7-.7"
        stroke={color}
        strokeWidth={1.1}
        strokeLinecap="round"
      />
    </Svg>
  );
}

function IconSettings({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Circle cx={7} cy={7} r={1.6} stroke={color} strokeWidth={1.1} />
      <Path
        d="M7 1.5v1.6M7 10.9v1.6M12.5 7h-1.6M3.1 7H1.5M10.9 3.1l-1.1 1.1M4.2 9.8l-1.1 1.1M10.9 10.9l-1.1-1.1M4.2 4.2L3.1 3.1"
        stroke={color}
        strokeWidth={1.1}
        strokeLinecap="round"
      />
    </Svg>
  );
}

function IconEditor({ color }: { color: string }) {
  // Pencil — used as the "Edit case" icon for Licensing programs.
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Path
        d="M9.5 2L12 4.5L4.5 12H2V9.5L9.5 2z"
        stroke={color}
        strokeWidth={1.1}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <Path d="M8.5 3L11 5.5" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
    </Svg>
  );
}

function IconDeliverables({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Path
        d="M3 1.5h5l3 3V12a.5.5 0 0 1-.5.5h-7A.5.5 0 0 1 3 12V2a.5.5 0 0 1 .5-.5z"
        stroke={color}
        strokeWidth={1.1}
        strokeLinejoin="round"
      />
      <Path d="M8 1.5V4.5h3" stroke={color} strokeWidth={1.1} strokeLinejoin="round" />
      <Path d="M5 7.5h4M5 9.5h3" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
    </Svg>
  );
}

function IconPositions({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Rect x={2} y={4} width={10} height={8} rx={1} stroke={color} strokeWidth={1.1} />
      <Path d="M5 4V2.5h4V4" stroke={color} strokeWidth={1.1} strokeLinecap="round" />
    </Svg>
  );
}

function IconPipeline({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Path d="M2 3h10M2 7h7M2 11h4" stroke={color} strokeWidth={1.2} strokeLinecap="round" />
      <Circle cx={11.5} cy={11} r={1.6} stroke={color} strokeWidth={1.1} />
    </Svg>
  );
}

function IconShortlist({ color }: { color: string }) {
  return (
    <Svg width={14} height={14} viewBox="0 0 14 14" fill="none">
      <Path
        d="M7 1.5l1.6 3.4 3.7.4-2.7 2.6.7 3.7L7 9.9 3.7 11.6l.7-3.7L1.7 5.3l3.7-.4L7 1.5z"
        stroke={color}
        strokeWidth={1.1}
        strokeLinejoin="round"
      />
    </Svg>
  );
}
